import { mountCustomFrame } from './renderers/custom-frame';
import type { GuideRenderContext, GuideStep, InlinePosition, StepBlock } from './types';

/** Destino de una inyección CRUDA: junto a `anchor`, en la posición `pos`. */
export interface RawInlineTarget {
  anchor: Element;
  pos: InlinePosition;
}

/** Inserta nodos CRUDOS (sin wrapper) junto al ancla, preservando el orden. */
function insertRawNodes(anchor: Element, nodes: ChildNode[], pos: InlinePosition): void {
  const parent = anchor.parentNode;
  if (pos === 'prepend') {
    const first = anchor.firstChild;
    for (const n of nodes) anchor.insertBefore(n, first);
  } else if (pos === 'append') {
    for (const n of nodes) anchor.appendChild(n);
  } else if (pos === 'before') {
    for (const n of nodes) parent?.insertBefore(n, anchor);
  } else {
    // after: insertar cada uno tras el anterior para no invertir el orden.
    let ref: ChildNode = anchor;
    for (const n of nodes) {
      parent?.insertBefore(n, ref.nextSibling);
      ref = n;
    }
  }
}

/**
 * Ejecutor de un bloque `code`.
 *
 * ⚠️ **Excepción sancionada #3 a la regla no-eval/no-innerHTML del SDK**
 * (ver CLAUDE.md regla 8). El bloque `code` es un escape hatch DELIBERADO tipo
 * Google Tag Manager: corre código del autor con acceso a la página host. El
 * usuario lo aprobó explícitamente asumiendo el riesgo (es SU app).
 *
 * Dos modos:
 *  - `sandboxed: true` → iframe aislado (origen null, sin acceso al host). Es
 *    el comportamiento viejo de las guías `custom`; reusa `custom-frame.ts`.
 *  - `sandboxed: false` (default) → modo HOST: el CSS/JS se inyecta en la página
 *    real (el JS con acceso total al DOM host, tipo GTM). La HTML va al LIGHT DOM
 *    (hijo del host de la guía) y se PROYECTA en la tarjeta con un `<slot>`, así
 *    la estiliza el CSS de la PLATAFORMA de forma nativa (clases + icon fonts).
 *    Los `<script>` del HTML se re-crean en el host para que ejecuten. Todo lo
 *    inyectado se limpia al desmontar el paso.
 */
export interface MountedCode {
  /** Nodo visible a insertar en la card (o `null` en modo host sin HTML). */
  node: HTMLElement | null;
  cleanup: () => void;
}

/** Secuencia para nombres de slot únicos por bloque code. */
let codeSlotSeq = 0;

export function mountCodeBlock(
  block: StepBlock,
  doc: Document,
  context: GuideRenderContext,
  /** Host (light DOM) de la guía: la HTML host se proyecta ahí vía `<slot>` para
   *  que el CSS de la plataforma aplique nativo. */
  host?: HTMLElement,
  /** Inyección CRUDA: inserta la HTML directo en el DOM real junto al ancla (sin
   *  wrappers/shadow) para DUPLICAR un elemento nativo tal cual — contexto,
   *  layout e iconos incluidos. Lo usa una guía inline de un solo bloque code. */
  rawInline?: RawInlineTarget,
): MountedCode {
  if (block.sandboxed) {
    const step = { html: block.html ?? '', css: block.css, js: block.js } as GuideStep;
    const { iframe, cleanup } = mountCustomFrame(doc, step, context, { width: '100%' });
    const wrapper = doc.createElement('div');
    wrapper.className = 'veo-code-sandbox';
    wrapper.appendChild(iframe);
    return { node: wrapper, cleanup };
  }

  // ── Modo host: acceso total al DOM real ──
  // Se ejecuta SIEMPRE en la página real, SIN iframe — en runtime, en el link de
  // preview y también en el editor. Es un pedido explícito del usuario: quiere
  // ver el código correr de verdad al previsualizar. (Al re-renderizar la preview
  // el cleanup remueve la inyección previa, así no se acumula.)
  const hostDoc = doc; // el ownerDocument del shadow root ES el documento host
  const injected: ChildNode[] = [];

  if (typeof block.css === 'string' && block.css.trim()) {
    const style = hostDoc.createElement('style');
    style.textContent = block.css;
    hostDoc.head.appendChild(style);
    injected.push(style);
  }

  let node: HTMLElement | null = null;
  if (typeof block.html === 'string' && block.html.trim()) {
    // EXCEPCIÓN #3: innerHTML sobre un nodo DETACHED (inerte: no ejecuta scripts).
    // Los `<script>` se re-crean en el host para que corran (tipo GTM).
    const holder = doc.createElement('div');
    holder.className = 'veo-code-html';
    holder.innerHTML = block.html;
    for (const old of Array.from(holder.querySelectorAll('script'))) {
      injected.push(runScript(old, hostDoc));
      old.remove();
    }
    if (rawInline) {
      // DUPLICAR un elemento nativo: los nodos van CRUDOS al DOM real junto al
      // ancla (sin el holder ni el host de la guía), así toman el contexto/layout
      // de la plataforma tal cual (selectores `>`, flex, etc.).
      const rawNodes = Array.from(holder.childNodes);
      insertRawNodes(rawInline.anchor, rawNodes, rawInline.pos);
      injected.push(...rawNodes);
      // `node` queda null: no hay tarjeta.
    } else if (host) {
      // La HTML debe verse TAL CUAL la web (clases nativas + icon fonts). Va al
      // LIGHT DOM (hijo del host de la guía) donde el CSS de la plataforma aplica
      // de forma NATIVA, y se PROYECTA dentro de la tarjeta con un `<slot>`. Sin
      // clonar hojas de estilo (evita el async/parpadeo del enfoque anterior).
      // Nota: selectores que dependen de un ancestro (`.sidebar .side-nav-item`)
      // no matchean acá (el ancestro es el host, no `.sidebar`); los de clase sí.
      const slotName = `veo-code-${++codeSlotSeq}`;
      holder.slot = slotName;
      host.appendChild(holder);
      injected.push(holder);
      const slot = doc.createElement('slot');
      slot.setAttribute('name', slotName);
      node = slot;
    } else {
      // Fallback sin host: render directo (dentro del shadow, sin estilos de la
      // plataforma). No debería pasar en el runtime/preview reales.
      node = holder;
    }
  }

  if (typeof block.js === 'string' && block.js.trim()) {
    const s = hostDoc.createElement('script');
    s.textContent = block.js;
    hostDoc.body.appendChild(s);
    injected.push(s);
  }

  return {
    node,
    cleanup: () => {
      for (const n of injected) n.parentNode?.removeChild(n);
    },
  };
}

/** Re-crea un `<script>` (inerte al venir de innerHTML) para que EJECUTE en el host. */
function runScript(old: HTMLScriptElement, hostDoc: Document): HTMLScriptElement {
  const s = hostDoc.createElement('script');
  for (const attr of Array.from(old.attributes)) s.setAttribute(attr.name, attr.value);
  if (!old.src) s.textContent = old.textContent;
  hostDoc.body.appendChild(s);
  return s;
}
